/*
###################################################################
Fitinsur Open Insurance - Copyright 2019-2023 - All Rights Reserved
###################################################################
*/

import * as yup from 'yup';
import { cloneDeep } from 'lodash';
import { accessGroupHandler } from '../../accessGroup';

function handleInitialAdjustments({
  questionnaire
}) {
  try {
    return questionnaire.map(question => {
      return question;
    })
  } catch (e) {
    console.log("GENERAL - handleInitialAdjustments error", e)
  }
}

function sectionIsVisible({
  sectionName,
  questionnaire,
  permissions = {},
  currentModality,
  ignoreVisibilityByModality = false
}) {
  const permissionsValues = Object.values(permissions);

  return questionnaire
    .some(question => question?.behaviorObject?.sectionName === sectionName
      && question.visibleQuestion
      && (ignoreVisibilityByModality || !question.productPlans?.length || question.productPlans.includes(currentModality))
      && (
        !question?.behaviorObject?.idDomainAccessFilter ||
        !permissionsValues ||
        (!Array.isArray(question?.behaviorObject?.idDomainAccessFilter) ?
          permissionsValues.includes(question.behaviorObject.idDomainAccessFilter) :
          permissionsValues.find(v => v && question?.behaviorObject?.idDomainAccessFilter.includes(v))
        )
      )
    )
}

function handleQuestionnaire({
  data,
  setQuestionnaire,
  setFieldValue,
  accessGroupData,
  idAccessGroup,
  setIsExternal
}) {
  let questionnaire = [];
  questionnaire = [
    ...data.persons,
    ...data.operations,
    ...data.payments,
    ...data.risks
  ];

  // 0 - GENERAL initial adjustments
  questionnaire = handleInitialAdjustments({ questionnaire })

  // 1 - ACCESS_GROUP initial adjustments
  questionnaire = accessGroupHandler.events.handleInitialAdjustments({
    questionnaire,
    setFieldValue,
    accessGroupData,
    idAccessGroup,
    setIsExternal
  });

  questionnaire = questionnaire.sort((a, b) => a.sequenceQuestion - b.sequenceQuestion);

  setQuestionnaire(questionnaire);

  return questionnaire;
}

function handleSave({
  values,
  questionnaire,
  idAccessGroup,
  idOperation,
  idOperationParent,
  SECTIONS,
  rolesGroup,
  usersGroup,
  isDraft,
  getDescription,
  sentUpdateAdminOperation,
  useUpdateAdminOperationRequest,
  mc,
  formikRef,
  history,
}) {

  const groupMemberships = [...rolesGroup, ...usersGroup].filter(item => !item.isExpired);

  buildValidation({
    questionnaire,
    idAccessGroup,
    idOperation,
    idOperationParent,
    SECTIONS,
    rolesGroup,
    usersGroup,
    isDraft,
    getDescription,
    useUpdateAdminOperationRequest,
  }).validate({...values, groupMemberships }, { abortEarly: false }).then(() => {
    const isValidSubmittedForm = handleValidationsSubmittedForm({ isDraft });
    
    if (isValidSubmittedForm) handleSubmit({
      values,
      questionnaire,
      idAccessGroup,
      idOperation,
      idOperationParent,
      SECTIONS,
      rolesGroup,
      usersGroup,
      sentUpdateAdminOperation,
      useUpdateAdminOperationRequest,
      history,
      mc,
      getDescription
    });

  }).catch((error) => {
    console.log("error buildValidation", error)
    onValidationError({
      error,
      mc,
      formikRef,
      questionnaire,
      getDescription
    })
  })
}

function buildValidation({
  questionnaire,
  idAccessGroup,
  idOperation,
  idOperationParent,
  SECTIONS,
  rolesGroup,
  usersGroup,
  isDraft,
  getDescription,
  useUpdateAdminOperationRequest,
}) {
  const validationSchema = {};
  const validationQuestionnaire = handleSaveQuestionnaireAnswer({
    questionnaire,
    idAccessGroup,
    idOperation,
    idOperationParent,
    onlyQuestions: true,
    SECTIONS,
    rolesGroup,
    usersGroup,
    useUpdateAdminOperationRequest,
  });

  let allQuestions = [...validationQuestionnaire];

  for (const question of allQuestions) {
    if (isDraft && ![].includes(question.idQuestion)) continue;
    if ([614].includes(question?.idDomainQuestion)) continue;
    if (!question?.key) continue;

    question?.childrenQuestions?.map(item => {
      if (item.visibleQuestion && item.fieldMask?.type == 'phone' && item.behaviorObject?.isRequired) {
        validationSchema[item.key] = yup.string().test({
          name: "answer-validation",
          message: getDescription("DOMAINS", 765),
          test: (value) => !!value && value.length >= 10
        })
      } else if (item.visibleQuestion && item.behaviorObject?.fieldMask == 'email') {
        if (item.answer) {
          validationSchema[item.key] = yup.string().email(getDescription("DOMAINS", 763)).required(getDescription("DOMAINS", 762))
        } else {
          if (item.behaviorObject?.isRequired) {
            validationSchema[item.key] = yup.string().email(getDescription("DOMAINS", 763)).required(getDescription("DOMAINS", 762))
          } else {
            validationSchema[item.key] = yup.string().notRequired();
          }
        }
      } else {
        validationSchema[item.key] = item.visibleQuestion && item.behaviorObject?.isRequired
          ? yup.string().test("answer-validation", getDescription("DOMAINS", 762), function (value) {
            return !!item.answer;
          }) : yup.string().notRequired();
      }
    })

    if (question.visibleQuestion && question.behaviorObject?.isRequired && question.fieldMask?.type == 'phone') {
      validationSchema[question.key] = yup.string().test({
        name: "answer-validation",
        message: getDescription("DOMAINS", 765),
        test: (value) => !!value && value.length >= 10
      })
    } else if (question.visibleQuestion && question.behaviorObject?.fieldMask == 'email') {
      if (question.answer) {
        validationSchema[question.key] = yup.string().email(getDescription("DOMAINS", 763)).required(getDescription("DOMAINS", 762))
      } else {
        if (question.behaviorObject?.isRequired) {
          validationSchema[question.key] = yup.string().email(getDescription("DOMAINS", 763)).required(getDescription("DOMAINS", 762))
        } else {
          validationSchema[question.key] = yup.string().notRequired();
        }
      }
    } else {
      validationSchema[question.key] = question.visibleQuestion && question.behaviorObject?.isRequired
        ? yup.string().test("answer-validation", getDescription("DOMAINS", 762), function (value) {
          return !!question.answer;
        }) : yup.string().notRequired();
    }
  }
  validationSchema["groupMemberships"] = yup.array().min(1, "Por favor, atribua ao menos um perfil de acesso e um usuário ao grupo.") //getDescription("DOMAINS", 872)
  return yup.object().shape(validationSchema);
}

function handleValidationsSubmittedForm({
  isDraft
}) {
  if (isDraft) return true;

  let validationResult = true;

  return validationResult;
}

async function handleSubmit({
  values,
  questionnaire,
  idAccessGroup,
  idOperation,
  idOperationParent,
  SECTIONS,
  rolesGroup,
  usersGroup,
  sentUpdateAdminOperation,
  useUpdateAdminOperationRequest,
  history,
  mc,
  getDescription
}) {
  if (sentUpdateAdminOperation.current) return;

  sentUpdateAdminOperation.current = true;
  
  handleSaveQuestionnaireAnswer({
    questionnaire,
    idAccessGroup,
    idOperation,
    idOperationParent,
    onlyQuestions: false,
    SECTIONS,
    rolesGroup,
    usersGroup,
    useUpdateAdminOperationRequest,
    history,
    sentUpdateAdminOperation,
    mc,
    getDescription
  });
}

function handleSaveQuestionnaireAnswer({
  questionnaire,
  idAccessGroup,
  idOperation,
  idOperationParent,
  onlyQuestions,
  SECTIONS,
  rolesGroup,
  usersGroup,
  useUpdateAdminOperationRequest,
  history,
  sentUpdateAdminOperation,
  mc,
  getDescription
}) {
  let questionnaireAnswers = cloneDeep(questionnaire);

  let childrenQuestionIds = [];
  for (const question of questionnaireAnswers) {
    if (question) {
      if (
        (question.idDomainQuestion === 270 ||
          question.idDomainQuestion === 271 ||
          (question.idDomainQuestion === 44 && typeof question.answer === 'object')
        ) && question.answer
      ) {
        try {
          question.answer = JSON.stringify(question.answer)
        } catch (e) {
        }
      }

      if (SECTIONS[question.behaviorObject?.sectionName] && SECTIONS[question.behaviorObject?.sectionName].isMultiple) {
        if (question?.childrenQuestions?.length) {
          const idQuestions = question.childrenQuestions.map(question => question.idQuestion);
          childrenQuestionIds = [...childrenQuestionIds, ...idQuestions];
        }
      } else {
        question.childrenQuestions = [];
      }
    }
  }

  questionnaireAnswers = questionnaireAnswers.filter(v => !childrenQuestionIds.includes(v.idQuestion))

  if (onlyQuestions) return questionnaireAnswers;

  const accessGroupMemberships = [...usersGroup, ...rolesGroup];
 
  useUpdateAdminOperationRequest({
    idGroup: idAccessGroup,
    idOperation,
    idOperationParent,
    questionnaire: questionnaireAnswers,
    accessGroupMemberships
  }).then((resp) => {
    if (resp.status === 200) {
      const { data: { value } } = resp;

      if (value?.redirectURL) {
        history.push(value.redirectURL);
        window.location.reload();
      } else {
        const { idAccessGroup, idOperation } = value;
        if(idAccessGroup && idOperation){
          mc.success(getDescription("DOMAINS", 873));
          history.push(`/management/58/access-group`);
        }
      }
    }
  }).finally(() => {
    sentUpdateAdminOperation.current = false;
  });
}

function onValidationError({
  error,
  mc,
  formikRef,
  questionnaire,
  getDescription
}) {
  questionnaire = cloneDeep(questionnaire);
  mc.error(getDescription("DOMAINS", 760))
  const allQuestions = questionnaire;

  let firstErrorKey = null;
  try {
    for (const question of allQuestions) {
      if (!formikRef.current.errors || !formikRef.current.errors[question.key]) continue;
      formikRef.current.setFieldError(question.key, null);
    }

    error.inner.forEach(({ message, path }) => {
      let question = allQuestions.find(question => question.key === path);
      
      if (question && question.dsQuestion !== "E-mail") mc.error(getDescription("DOMAINS", 764).replace('@@dsQuestion', question.dsQuestion));
      else path !== "groupMemberships" && mc.error(getDescription("DOMAINS", 761));

      if (!message.includes(["E-mail", "Telefone"])) formikRef.current.setFieldError(question?.key, `* ${getDescription("DOMAINS", 762)}`);
      else formikRef.current.setFieldError(question?.key, `* ${getDescription("DOMAINS", 763)}`);
      
      if(path === "groupMemberships"){
        mc.error(getDescription("DOMAINS", 872) || "Por favor, atribua ao menos um perfil de acesso ou um usuário ao grupo.");
        const qstKey = allQuestions.find(qst=> qst.idQuestion == 1980);
        question = qstKey;
      }

      if (!firstErrorKey){ 
        firstErrorKey = question.key;
      }
    })

    if (firstErrorKey) {
      const elements = global.window.document.getElementsByName(firstErrorKey);
      if (elements?.length) {
        setTimeout(() => elements[0].scrollIntoView({ behavior: 'smooth', block: 'start', inline: 'start' }), 300);
      }
    }
  } catch (error) {
    console.log('onValidationError catch', error)
  }
}

const events = {
  handleInitialAdjustments,
  handleQuestionnaire,
  sectionIsVisible,
  handleSave,
}

export { events };